<script setup>
import { useAttrs, ref, watch, onMounted, computed, defineProps, defineEmits } from 'vue';

const props = defineProps({
  ...propsDefinition,
  options: {
    type: Array,
    default() {
      return [];
    }
  }
});
const emit = defineEmits(['input']);
const $wrapper = ref(null);

// == Model Value =======================================
const getIntialValue = () => props.value;
const value = ref(null);
watch(
  () => props.value,
  (newValue) => {
    value.value = newValue;
  }
);
watch(value, (newValue, oldValue) => {
  emit('input', newValue, oldValue);
});

// == Attributes =======================================
const attributes = useAttrs();
const inputAttributes = computed(() => {
  const _inputAttributes = {};
  Object.entries(attributes).forEach(([key, value]) => {
    const ignored = ['class', 'style'];
    if (ignored.includes(key)) {
      return;
    }

    _inputAttributes[key] = value;
  });

  return _inputAttributes;
});

// == Setup =======================================
onMounted(() => {
  value.value = getIntialValue();
});
</script>

<script>
export default {
  inheritAttrs: false
}

export const propsDefinition = {
  value: {
    type: [Array, Number, String, Object, Boolean],
    default: null
  },

  name: {
    type: String,
    required: true
  },

  inputClass: {
    type: [String, Array, Object],
    default: undefined
  },

  label: {
    type: String,
    required: true
  },

  labelVisible: {
    type: Boolean,
    default: false
  },

  labelClass: {
    type: [String, Array, Object],
    default: ''
  },

  errorMessage: {
    type: String,
    default: ''
  },

  message: {
    type: String,
    default: ''
  },

  isValid: {
    type: Boolean,
    default: true
  }
}
</script>

<template>
  <div
    class="w-full"
    :class="[attributes.class, { 'has-error': errorMessage }]"
    :style="attributes.style"
    ref="$wrapper"
  >
    <label
      :for="name"
      :class="{
        'sr-only': !labelVisible,
        'text-brand-red': !!errorMessage,
        [labelClass]: labelClass
      }"
      class="block font-bold mb-1 text-brand-darkGray"
    >
      {{ label }}
    </label>
    <!-- Input Slot -->
    <slot v-bind="props" :inputAttributes="inputAttributes" :props="props"></slot>
    <div v-if="message && !errorMessage" class="mt-1 flex items-start text-sm italic text-gray-500 font-sans">{{ message }}</div>
    <div v-if="errorMessage" class="mt-1 flex items-start text-sm italic text-brand-red font-sans">{{ errorMessage }}</div>
  </div>
</template>
